// First-time setup for a home patient, one step at a time, in large type. Which steps
// there are, and which may be skipped, is set per patient by the study team (mandatory /
// recommended / not required; backend/vtlite/home_setup.py). It opens by itself at the
// first sign-in (and after the team asks for it again), and from "Set up again" any time.

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, type Qualification, type QualifyCheck, type SetupStatus, type SetupStepKey } from "../api";
import { useAuth } from "../auth";
import { CardCheck } from "../components/CardCheck";
import { FixCheck, detectPlatform } from "../components/HomeChecks";
import { LuxGuide } from "../components/LuxGuide";
import { Practice } from "../components/Practice";
import { ChromeAllowPicture, CordPicture, HelperPicture } from "../components/SetupPictures";
import { errorText } from "../hooks";
import { probe, screenDetails } from "../lib/probe";

const STATUS_TEXT: Record<string, string> = {
  done: "done", skipped: "skipped", todo: "", not_required: "not needed", before_setup: "done earlier",
};

/** What a refused step said, with the checks to fix if it sent any. */
interface Refusal { message: string; checks: QualifyCheck[] }

function refusal(e: unknown): Refusal {
  if (e instanceof ApiError && e.status === 409 && e.detail && typeof e.detail === "object") {
    const d = e.detail as { message?: string; checks?: QualifyCheck[] };
    return { message: d.message ?? "Not yet.", checks: d.checks ?? [] };
  }
  return { message: errorText(e), checks: [] };
}

export function SetupPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const again = params.get("again") === "1";
  const [st, setSt] = useState<SetupStatus | null>(null);
  const [current, setCurrent] = useState<SetupStepKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (s: SetupStatus, after?: SetupStepKey) => {
    const shown = s.steps.filter((x) => x.level !== "off");
    if (again && after === undefined) return shown[0]?.key ?? null;
    if (again) {
      const i = shown.findIndex((x) => x.key === after);
      return shown[i + 1]?.key ?? null;
    }
    return shown.find((x) => x.status === "todo")?.key ?? null;
  };
  useEffect(() => {
    api.mySetup().then((s) => { setSt(s); setCurrent(pick(s)); }, (e) => setError(errorText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finished = (s: SetupStatus, step: SetupStepKey) => {
    setSt(s);
    setCurrent(pick(s, step));
    window.scrollTo(0, 0);
  };

  if (error) return <p className="error">{error}</p>;
  if (!st || !user) return <div>Loading…</div>;
  const shown = st.steps.filter((x) => x.level !== "off");
  const step = shown.find((x) => x.key === current);
  const n = shown.findIndex((x) => x.key === current);
  return (
    <div className="home-test setup-page">
      <h1>{again ? "Set up again" : "Before your first test"}</h1>
      <p>{again ? "Go through any of these again. " : "A few things to get ready, one at a time. "}
        A family member may help with all of it.</p>
      <ol className="setup-progress">
        {shown.map((x) => (
          <li key={x.key} className={x.key === current ? "now" : x.status}>
            <button className="link" onClick={() => setCurrent(x.key)}>{x.title}</button>
            {x.level === "recommended" && <span className="small muted"> (recommended)</span>}
            {STATUS_TEXT[x.status] && <span className="small"> — {STATUS_TEXT[x.status]}</span>}
          </li>
        ))}
      </ol>
      {step ? (
        <div className="card setup-step">
          <h2 style={{ marginTop: 0 }}>Step {n + 1} of {shown.length}: {step.title}</h2>
          <StepBody key={step.key} stepKey={step.key} st={st} optional={step.level !== "mandatory"}
            onDone={(s) => finished(s, step.key)} />
        </div>
      ) : (
        <div className="card ok-card">
          <h2 style={{ marginTop: 0 }}>{st.blocking.length ? "Almost there" : "You’re ready"}</h2>
          {st.blocking.length > 0 ? (
            <p>Still needed: {st.steps.filter((x) => st.blocking.includes(x.key)).map((x) => x.title).join(", ")}.</p>
          ) : <p>Thank you. Each test from now on starts with a quick check of the screen.</p>}
          {user.patient_id !== null && st.blocking.length === 0 && (
            <button className="big" onClick={() => nav(`/patients/${user.patient_id}/new-session`)}>Go to my test</button>
          )}
        </div>
      )}
      <p><Link to="/">Back to the start page</Link></p>
    </div>
  );
}

function StepBody({ stepKey, st, optional, onDone }: {
  stepKey: SetupStepKey; st: SetupStatus; optional: boolean; onDone: (s: SetupStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const send = async (body: Parameters<typeof api.setupStep>[1]) => {
    setBusy(true);
    setRefused(null);
    try {
      onDone(await api.setupStep(stepKey, body));
    } catch (e) {
      setRefused(refusal(e));
    } finally {
      setBusy(false);
    }
  };
  const skip = optional ? () => void send({ skip: true }) : undefined;
  const shownRefusal = refused && (
    <div className="card bad-card">
      <p style={{ marginTop: 0 }}><strong>{refused.message}</strong></p>
      {refused.checks.map((c, i) => <FixCheck key={c.key} check={c} open={i === 0} />)}
    </div>
  );
  const body = {
    chrome: <ChromeStep busy={busy} send={send} />,
    monitor_helper: <HelperStep busy={busy} send={send} steps={st.helper_steps ?? {}} />,
    display: <DisplayStep busy={busy} send={send} brightness={st.requirements.session.brightness !== "off"} />,
    card_size: <CardStep busy={busy} send={send} st={st} skip={skip} />,
    practice: <PracticeStep busy={busy} send={send} skip={skip} />,
  }[stepKey];
  return (
    <>
      {body}
      {shownRefusal}
      {skip && stepKey !== "card_size" && stepKey !== "practice" && (
        <p><button className="secondary" disabled={busy} onClick={skip}>Skip this step for now</button></p>
      )}
    </>
  );
}

type Send = (body: Parameters<typeof api.setupStep>[1]) => Promise<void>;

function ChromeStep({ busy, send }: { busy: boolean; send: Send }) {
  const go = async () => {
    const d = await screenDetails();
    await send({ probe: await probe(d?.current ?? null) });
  };
  return (
    <>
      <p>The test needs <strong>Google Chrome</strong>, and Chrome needs to know which screen it is drawing on.</p>
      <ol className="steps">
        <li>Click <strong>Ask Chrome</strong> below.</li>
        <li>Chrome shows a small box at the top-left of this window, under the address bar.</li>
        <li>Click <strong>Allow</strong>. Chrome remembers it.</li>
      </ol>
      <ChromeAllowPicture />
      <button className="big" disabled={busy} onClick={() => void go()}>{busy ? "Checking…" : "Ask Chrome"}</button>
    </>
  );
}

function HelperStep({ busy, send, steps }: {
  busy: boolean; send: Send; steps: Partial<Record<"mac" | "windows", string[]>>;
}) {
  const plat = detectPlatform();
  const go = async () => {
    const d = await screenDetails();
    await send({ probe: await probe(d?.current ?? null) });
  };
  const forPlatform = (p: "mac" | "windows", label: string) => (
    <>
      <p><a className="button big" href={api.monitorHelperUrl(p)}>{label}</a></p>
      <ol className="steps fix-steps">{(steps[p] ?? []).map((s, i) => <li key={i}>{s}</li>)}</ol>
    </>
  );
  return (
    <>
      <p>A small download reads your monitor’s own identity (maker, model, serial number, size, and how many bits of
        colour the computer sends it) and sends it to the study. It changes nothing on your computer. Keep the monitor you
        test on connected, and this window on it.</p>
      <HelperPicture />
      {plat !== "windows" && forPlatform("mac", "Download for Mac")}
      {plat !== "mac" && forPlatform("windows", "Download for Windows")}
      <p className="small muted">Each download works once, for 30 minutes.</p>
      <button className="big" disabled={busy} onClick={() => void go()}>I’ve run it: check</button>
    </>
  );
}

function DisplayStep({ busy, send, brightness }: { busy: boolean; send: Send; brightness: boolean }) {
  const [q, setQ] = useState<Qualification | null>(null);
  const [rep, setRep] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const check = async () => {
    setChecking(true);
    setErr(null);
    try {
      const d = await screenDetails();
      const r = await probe(d?.current ?? null);
      setRep(r);
      setQ(await api.qualify(r));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setChecking(false);
    }
  };
  const failed = q?.checks.filter((c) => c.critical && !c.ok) ?? [];
  return (
    <>
      <p>Set the monitor up the way it will always be used, then check it{brightness ? " and measure its brightness once" : ""}.</p>
      <ol className="steps">
        <li>Use the monitor at its own (native) resolution, with HDR off.</li>
        <li>Set the picture mode and brightness the study gave you{q?.instructions ? <>: <strong>{q.instructions}</strong></> : ""}.</li>
        <li>Switch the monitor on 15 minutes before measuring.</li>
      </ol>
      {!q && <button className="big" disabled={checking} onClick={() => void check()}>{checking ? "Checking…" : "Check my screen"}</button>}
      {q && failed.length > 0 && (
        <div className="card bad-card">
          <h3 style={{ marginTop: 0 }}>{failed.length === 1 ? "One thing to fix first" : `${failed.length} things to fix`}</h3>
          {failed.map((c, i) => <FixCheck key={c.key} check={c} open={i === 0} />)}
          <button className="big" disabled={checking} onClick={() => void check()}>Check again</button>
        </div>
      )}
      {q && failed.length === 0 && !brightness && (
        <button className="big" disabled={busy} onClick={() => void send({ probe: rep })}>My screen is set up</button>
      )}
      {q && failed.length === 0 && brightness && q.brightness && (
        <LuxGuide variant={q.brightness.phase === "baseline" ? "full" : "short"} instructions={q.brightness.instructions}
          title={q.brightness.phase === "baseline" ? "Your reading is saved as this monitor's normal brightness."
            : "This monitor already has its normal brightness: check it matches."}
          judge={(r) => api.brightnessCheck(r, { edid_key: q.brightness!.edid_key, screen_key: q.brightness!.screen_key })}
          onDone={(r) => void send({ probe: rep, readings_lux: r })} />
      )}
      {err && <p className="error">{err}</p>}
    </>
  );
}

function CardStep({ busy, send, st, skip }: { busy: boolean; send: Send; st: SetupStatus; skip?: () => void }) {
  const [q, setQ] = useState<Qualification | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const d = await screenDetails();
      setQ(await api.qualify(await probe(d?.current ?? null)));
    })().catch((e) => setErr(errorText(e)));
  }, []);
  if (err) return <p className="error">{err}</p>;
  if (!q) return <p>Getting the screen’s size…</p>;
  if (!q.screen_size_mm || !q.screen_px) {
    return (
      <>
        <p>This screen’s size is not known yet: finish the screen check first.</p>
        {skip && <button className="secondary" onClick={skip}>Skip for now</button>}
      </>
    );
  }
  return (
    <>
      <p>This checks that the screen shows true sizes: nothing zoomed or magnified.</p>
      <CardCheck screenSizeMm={q.screen_size_mm} screenPx={q.screen_px} cardMm={st.card_mm} tolerance={st.card_tolerance}
        busy={busy} onDone={(scale) => void send({ scale })} onSkip={skip} />
    </>
  );
}

function PracticeStep({ busy, send, skip }: { busy: boolean; send: Send; skip?: () => void }) {
  const [cordOk, setCordOk] = useState(false);
  return !cordOk ? (
    <>
      <p>Each test is done at the distance the study set, measured with the cord in your kit.</p>
      <CordPicture metres={1} />
      <ol className="steps">
        <li>Hold one end of the cord against the middle of the screen.</li>
        <li>Sit so the other end just reaches your eyes, with the cord straight.</li>
        <li>Wear your usual glasses for distance.</li>
      </ol>
      <div className="row">
        <button className="big" onClick={() => setCordOk(true)}>I’m sitting at the cord’s length</button>
        {skip && <button className="secondary" onClick={skip}>Skip for now</button>}
      </div>
    </>
  ) : (
    <Practice busy={busy} onDone={(r) => void send({ practice: r })} onSkip={skip} />
  );
}
